import React, { useMemo } from "react";
import type { CityWeather } from "../agendaWeatherTypes";
import { normalizeWeatherHourKey } from "../agendaWeatherHourKey";
import WeatherHourCell from "./WeatherHourCell";
import { resolveWeatherRowText } from "../weatherRowText";

type WeatherRowProps = {
  businessHours: string[];
  cityWeather: CityWeather | null;
  loading: boolean;
  failed: boolean;
  /** Past days show the observed weather (history) instead of a forecast. */
  isPastDate?: boolean;
};

const WeatherRow: React.FC<WeatherRowProps> = ({
  businessHours,
  cityWeather,
  loading,
  failed,
  isPastDate = false,
}) => {
  const hourMap = useMemo(() => {
    const map = new Map<string, CityWeather["hours"][number]>();
    cityWeather?.hours.forEach((hour) => {
      map.set(normalizeWeatherHourKey(hour.time), hour);
    });
    return map;
  }, [cityWeather]);

  const text = resolveWeatherRowText({ cityWeather, loading, failed, isPastDate });

  return (
    <>
      <div className="sticky left-0 z-[2] flex min-h-[32px] flex-col justify-center border-r border-[#EDF2F7] bg-[#F7FAFC] pr-2">
        <p className="min-w-0 text-[12px] font-semibold leading-tight text-[#4A5568]">
          {text.title}
        </p>
        {text.range ? (
          <p className="text-[11px] leading-tight text-[#718096]">{text.range}</p>
        ) : null}
      </div>

      {text.notice ? (
        // Spans the hour columns so the message never squeezes the name column.
        <div
          className="flex h-8 items-center px-2 text-[12px] text-[#718096]"
          style={{ gridColumn: `span ${Math.max(businessHours.length, 1)}` }}
          role="status"
        >
          {text.notice}
        </div>
      ) : (
        businessHours.map((hour) => {
          const key = normalizeWeatherHourKey(hour);
          return <WeatherHourCell key={`weather-${key}`} hour={hourMap.get(key)} />;
        })
      )}
    </>
  );
};

export default WeatherRow;
